import { type ValoriSezione } from '@framework/api-client/api/inquiries-compile/schemas/valoriSezione.ts';
import { type IEngineSection } from '@framework/engine/schema/sections/IEngineSection.ts';
import { type Widget } from '@framework/engine/schema/widgets/Widget.ts';
import { LoaderSpin } from '@framework/ui/components/loader/LoaderSpin.tsx';
import { type YupAnyObjectSchema } from '@framework/validation/YupObjectSchema.ts';
import { startTransition, useEffect, useState } from 'react';
import { FillableForm } from '../section/FillableForm.tsx';
import { ExternalSharedSection } from './ExternalSharedSection.tsx';
import { type ModuleFormSource } from './ModuleFormSource.ts';
import { SharedSectionVisibilityGate } from './shared-section/SharedSectionVisibilityGate.tsx';
import { SharedSectionWrapper } from './shared-section/SharedSectionWrapper.tsx';

export interface ISectionsFillableFormProps {
  sections: IEngineSection[];
  readonly: boolean;
  /** Permette di gestire dinamicamente il readonly per ogni singolo widget */
  isReadonly?: (widget: Widget) => boolean;
  moduleValues: ValoriSezione[];
  moduleSchema: YupAnyObjectSchema;
  /** Ambiente sorgente del modulo, per gestire correttamente le sezioni condivise esterne */
  moduleFormSource: ModuleFormSource;
}

/**
 * Componente in cui richiamiamo `FillableForm` al cui interno interroghiamo
 * il context `ModuleFormSectionsMonitorContext` tramite `useModuleFormSectionRuntime`
 * per sapere puntualmente se una sezione è visibile o meno gestendo correttamente
 * tutti i widget compresi quelli grafici.
 *
 * La classificazione della sezione condivisa è letta direttamente da
 * `section.shared`, popolato in fase di mappatura (FE: `useSections`).
 *
 * Il rendering delle sezioni avviene in modo progressivo: una sezione alla volta
 * tramite `startTransition` (React 18 concurrent mode). Ogni sezione viene
 * committata singolarmente, permettendo al browser di fare paint tra una e l'altra.
 */
export function SectionsFillableForm(props: ISectionsFillableFormProps) {
  const {
    sections,
    readonly,
    isReadonly,
    moduleValues,
    moduleSchema,
    moduleFormSource,
  } = props;
  const [visibleCount, setVisibleCount] = useState(0);

  useEffect(() => {
    if (visibleCount < sections.length) {
      startTransition(() => {
        setVisibleCount(prev => prev + 1);
      });
    }
  }, [visibleCount, sections.length]);

  if (visibleCount === 0 && sections.length > 0) {
    return <LoaderSpin />;
  }

  return (
    <>
      {sections.slice(0, visibleCount).map(section => {
        // (b) Sezione condivisa proveniente da un altro modulo → readonly con form separato
        if (section.shared?.type === 'external') {
          return (
            <SharedSectionVisibilityGate section={section} key={section.id}>
              <ExternalSharedSection
                section={section}
                moduleSchema={moduleSchema}
                moduleValues={moduleValues}
                moduleFormSource={moduleFormSource}
              />
            </SharedSectionVisibilityGate>
          );
        }

        // (a) Sezione condivisa di proprietà del modulo → editabile con avviso
        if (section.shared?.type === 'owned') {
          return (
            <SharedSectionVisibilityGate section={section} key={section.id}>
              <SharedSectionWrapper mode="editable">
                <FillableForm
                  section={section}
                  readonly={readonly}
                  isReadonly={isReadonly}
                />
              </SharedSectionWrapper>
            </SharedSectionVisibilityGate>
          );
        }

        // (c) Sezione non condivisa → form normale
        return (
          <FillableForm
            section={section}
            readonly={readonly}
            isReadonly={isReadonly}
            key={section.id}
          />
        );
      })}
      {visibleCount < sections.length && <LoaderSpin />}
    </>
  );
}
